// CashStore.jsx
// In-app purchase page for cash packs via Steam Inventory Service
// Flow: Open Steam overlay → User purchases → Sync inventory on return

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorldState } from '../components/state/WorldStateContext';
import { useAuth } from '../components/auth/AuthContext';
import { MONEY_PACKS, formatCashAmount } from '../data/MoneyPacks';
import { createCheckout, verifyPurchase, getSessionIdFromUrl, clearSessionIdFromUrl } from '../lib/stripe';
import { debug } from '../lib/debug.js';

// Approximate exchange rates (Steam handles actual conversion)
const EXCHANGE_RATES = {
  JPY: 150,
  CNY: 7.3,
};

function formatLocalPrice(priceUSD, language) {
  if (language === 'ja') {
    const jpy = Math.round(priceUSD * EXCHANGE_RATES.JPY);
    return `¥${jpy.toLocaleString()}`;
  }
  if (language === 'zh-CN' || language === 'zh') {
    const cny = (priceUSD * EXCHANGE_RATES.CNY).toFixed(0);
    return `¥${cny}`;
  }
  return `$${priceUSD.toFixed(2)}`;
}

function getLocalPricePerMillion(pack, language) {
  const millions = pack.cashAmount / 1_000_000;
  const perMillion = pack.priceUSD / millions;
  if (language === 'ja') {
    return `¥${(perMillion * EXCHANGE_RATES.JPY).toFixed(0)}`;
  }
  if (language === 'zh-CN' || language === 'zh') {
    return `¥${(perMillion * EXCHANGE_RATES.CNY).toFixed(2)}`;
  }
  return `$${perMillion.toFixed(3)}`;
}

function formatDisplayCash(amount) {
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(2)}B`;
  }
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  return `$${amount.toLocaleString()}`;
}

function PackCard({ pack, onPurchase, isPurchasing, t, language }) {
  const isBestValue = pack.bestValue;

  return (
    <div
      style={{
        position: 'relative',
        background: isBestValue
          ? 'linear-gradient(135deg, #1a3a1a 0%, #0d2818 100%)'
          : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        border: isBestValue ? '2px solid #22c55e' : '1px solid #334155',
        borderRadius: '12px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        minWidth: isBestValue ? '280px' : '200px',
        flex: isBestValue ? '1.4' : '1',
        boxShadow: isBestValue
          ? '0 0 30px rgba(34, 197, 94, 0.3)'
          : '0 4px 20px rgba(0, 0, 0, 0.3)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = isBestValue
          ? '0 0 40px rgba(34, 197, 94, 0.4)'
          : '0 8px 30px rgba(0, 0, 0, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isBestValue
          ? '0 0 30px rgba(34, 197, 94, 0.3)'
          : '0 4px 20px rgba(0, 0, 0, 0.3)';
      }}
    >
      {isBestValue && (
        <div
          style={{
            position: 'absolute',
            top: '-12px',
            right: '20px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: '#fff',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {t('cashStore.bestValue')}
        </div>
      )}

      <div
        style={{
          fontSize: isBestValue ? '20px' : '16px',
          fontWeight: 'bold',
          color: isBestValue ? '#22c55e' : '#e2e8f0',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {pack.name}
      </div>

      <div
        style={{
          fontSize: isBestValue ? '36px' : '28px',
          fontWeight: 'bold',
          color: '#fbbf24',
          textShadow: '0 2px 10px rgba(251, 191, 36, 0.3)',
        }}
      >
        {formatCashAmount(pack.cashAmount)}
      </div>

      {pack.wingsAmount > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            backgroundColor: '#2d1f4e',
            borderRadius: '12px',
            border: '1px solid #8b5cf640',
          }}
        >
          <span style={{ fontSize: '14px', color: '#a78bfa', fontWeight: 'bold' }}>
            +{pack.wingsAmount}
          </span>
          <span style={{ fontSize: '12px', color: '#8b5cf6' }}>{t('cashStore.wings')}</span>
        </div>
      )}

      <div
        style={{
          fontSize: '13px',
          color: '#94a3b8',
          textAlign: 'center',
          minHeight: '36px',
        }}
      >
        {pack.description}
      </div>

      <div style={{ fontSize: '11px', color: '#64748b' }}>
        {getLocalPricePerMillion(pack, language)}{t('cashStore.perMillion')}
      </div>

      <button
        onClick={() => onPurchase(pack)}
        disabled={isPurchasing}
        style={{
          width: '100%',
          padding: '12px 24px',
          background: isPurchasing
            ? '#475569'
            : isBestValue
              ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          border: 'none',
          borderRadius: '8px',
          color: '#fff',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: isPurchasing ? 'not-allowed' : 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        <span style={{ fontSize: '18px' }}>
          {isPurchasing ? t('cashStore.processing') : formatLocalPrice(pack.priceUSD, language)}
        </span>
        {!isPurchasing && (
          <span style={{ fontSize: '11px', opacity: 0.9 }}>
            {t('cashStore.clickToPurchase')}
          </span>
        )}
      </button>
    </div>
  );
}

export default function CashStore() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { worldState, addCash, addWings } = useWorldState();
  const { user } = useAuth();
  const [purchasingPackId, setPurchasingPackId] = useState(null);
  const [notification, setNotification] = useState(null);

  const currentCash = worldState?.airline?.cash || 0;

  // Check for Stripe redirect on mount (returning from Checkout)
  useEffect(() => {
    const sessionId = getSessionIdFromUrl();
    if (!sessionId || !user?.id) return;

    debug('[CashStore] Stripe session_id detected:', sessionId);
    clearSessionIdFromUrl();

    async function verifyAndGrant() {
      setNotification({ type: 'info', message: 'Verifying purchase...' });

      const result = await verifyPurchase(sessionId, user.id);

      if (result.success && result.grant) {
        const { cash, wings, pack_name } = result.grant;

        if (cash > 0 && !result.already_granted) addCash(cash);
        if (wings > 0 && !result.already_granted) addWings(wings);

        setNotification({
          type: 'success',
          message: `Purchase complete! +${formatCashAmount(cash)}${wings > 0 ? ` and +${wings} Wings` : ''}`,
        });

        debug('[CashStore] Granted:', { cash, wings, pack_name });
      } else {
        setNotification({
          type: 'error',
          message: result.error || 'Purchase verification failed.',
        });
      }
    }

    verifyAndGrant();
  }, [user?.id]);

  const handlePurchase = async (pack) => {
    if (!user?.id) {
      setNotification({ type: 'error', message: 'Please sign in to make purchases.' });
      return;
    }

    setPurchasingPackId(pack.id);
    setNotification({ type: 'info', message: 'Redirecting to checkout...' });

    const result = await createCheckout(pack.id, user.id);

    if (result.error) {
      setNotification({ type: 'error', message: result.error });
      setPurchasingPackId(null);
    }
    // If successful, browser redirects to Stripe — no further code runs
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        padding: '40px 20px',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
          }}
        >
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#f8fafc', margin: 0 }}>
              {t('cashStore.title')}
            </h1>
            <p style={{ color: '#94a3b8', margin: '8px 0 0 0' }}>
              {t('cashStore.subtitle')}
            </p>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#94a3b8', fontSize: '14px' }}>
              {t('cashStore.currentBalance')}
            </div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#22c55e' }}>
              {formatDisplayCash(currentCash)}
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div
            style={{
              padding: '16px 24px',
              borderRadius: '8px',
              marginBottom: '24px',
              background:
                notification.type === 'success'
                  ? 'rgba(34, 197, 94, 0.2)'
                  : notification.type === 'info'
                    ? 'rgba(59, 130, 246, 0.2)'
                    : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${
                notification.type === 'success'
                  ? '#22c55e'
                  : notification.type === 'info'
                    ? '#3b82f6'
                    : '#ef4444'
              }`,
              color:
                notification.type === 'success'
                  ? '#22c55e'
                  : notification.type === 'info'
                    ? '#3b82f6'
                    : '#ef4444',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              X
            </button>
          </div>
        )}


        {/* Pack Cards */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            justifyContent: 'center',
          }}
        >
          {MONEY_PACKS.map((pack) => (
            <PackCard
              key={pack.id}
              pack={pack}
              onPurchase={handlePurchase}
              isPurchasing={purchasingPackId === pack.id}
              t={t}
              language={i18n.language}
            />
          ))}
        </div>

        {/* Footer Info */}
        <div
          style={{
            marginTop: '48px',
            padding: '24px',
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '12px',
            border: '1px solid #334155',
          }}
        >
          <h3 style={{ color: '#e2e8f0', margin: '0 0 12px 0', fontSize: '16px' }}>
            {t('cashStore.aboutTitle')}
          </h3>
          <ul
            style={{
              color: '#94a3b8',
              fontSize: '14px',
              margin: 0,
              paddingLeft: '20px',
              lineHeight: '1.8',
            }}
          >
            <li>{t('cashStore.aboutItem1')}</li>
            <li>{t('cashStore.aboutItem2')}</li>
            <li>{t('cashStore.aboutItem3')}</li>
            <li>{t('cashStore.aboutItem4')}</li>
          </ul>
        </div>

        {/* Back Button */}
        <div style={{ marginTop: '32px', textAlign: 'center' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '12px 32px',
              background: 'transparent',
              border: '1px solid #475569',
              borderRadius: '8px',
              color: '#94a3b8',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {t('cashStore.backToGame')}
          </button>
        </div>
      </div>
    </div>
  );
}
